import type { FC } from "react";

import dayjs from "dayjs";
import queryString from "query-string";

import { SERPSnapshotTableTopWidgets } from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/SERPSnapshotTableTopWidgets";
import {
    DownloadExcelContainer,
    SearchContainer,
    TitleContainer,
} from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/StyledComponents";
import type { SERPRecords } from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/types";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { i18nFilter } from "app/filters/i18nFilter";
import { EBooleanSearchActions } from "app/pages/website-analysis/traffic-sources/search/BooleanSearchWithBothKeywordsAndWebsite";
import { BooleanSearchWithBothKeywordsAndWebsiteWrapper } from "app/pages/website-analysis/traffic-sources/search/BooleanSearchWithBothKeywordsAndWebsiteWrapper";
import {
    onChipAdd,
    onChipRemove,
} from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageUtillities";
import CountryService from "app/services/CountryService";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { allTrackers } from "app/services/track/track";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { StyledPrimaryTitle } from "pro-features/styled components/StyledBoxTitle/src/StyledBoxTitle";

interface ISERPSnapshotTableTop {
    serpRecords: SERPRecords;
    lastScrapeDate: string;
    excelAllowed: boolean;
    excelDownloadUrl: string;
    totalCount?: number;
    booleanSearchTerms?: string;
    tableColumns: Record<string, any>;
    filtersStateObject: any;
    isLoadingData: boolean;
    sort: string;
    noSerpRecords: boolean;
    asc: string;
    onClickToggleColumns(index: number): void;
    onFilterChange(value: Record<string, string>): void;
}

const DEFAULT_SORT = "position";

export const SERPSnapshotTableTop: FC<ISERPSnapshotTableTop> = (props) => {
    const {
        filtersStateObject,
        excelDownloadUrl,
        excelAllowed,
        serpRecords,
        noSerpRecords,
        lastScrapeDate,
        isLoadingData,
        sort = DEFAULT_SORT,
        asc = false,
        totalCount,
    } = props;
    const queryParams = { ...filtersStateObject, sort, asc };
    const excelLink = `${excelDownloadUrl}?${queryString.stringify(queryParams)}`;
    const isPaidExcelPermitted = isPaidExcelAllowed();
    const countryObject = CountryService.getCountryById(queryParams.country);
    const lastDateFormat = () => {
        if (lastScrapeDate) {
            return dayjs(lastScrapeDate).format("MMM DD, YYYY");
        }
    };

    const subtitleFilters = [
        {
            filter: "lastDate",
            value: lastDateFormat(),
        },
        {
            filter: "webSource",
            value: queryParams.webSource,
        },
        {
            filter: "country",
            countryCode: countryObject.id,
            value: countryObject.text,
        },
    ];

    const onBooleanSearchChange = (params) => {
        props.onFilterChange({ ExcludeUrls: params.ExcludeUrls, IncludeUrls: params.IncludeUrls });
    };

    const trackExcelDownload = () => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", "Table/Excel");
    };

    return (
        <>
            <TitleContainer>
                <div>
                    <StyledPrimaryTitle>
                        <BoxTitle>{i18nFilter()("serp.top.table.title")}</BoxTitle>
                    </StyledPrimaryTitle>
                    <StyledBoxSubtitle>
                        <BoxSubtitle filters={subtitleFilters} />
                    </StyledBoxSubtitle>
                </div>
                <DownloadExcelContainer>
                    <DownloadButtonMenu
                        Excel={true}
                        PaidExcel={isPaidExcelPermitted}
                        totalCount={totalCount}
                        downloadUrl={excelLink}
                        exportFunction={trackExcelDownload}
                        excelLocked={!excelAllowed}
                    />
                </DownloadExcelContainer>
            </TitleContainer>
            <SearchContainer>
                <BooleanSearchWithBothKeywordsAndWebsiteWrapper
                    placeholder={i18nFilter()("serp.boolean.search.placeholder")}
                    onChipAdd={onChipAdd}
                    onChipRemove={onChipRemove}
                    filtersStateObject={filtersStateObject}
                    booleanSearchAction={EBooleanSearchActions.WEBSITES}
                    onApplyChanges={onBooleanSearchChange}
                />
            </SearchContainer>
            <SERPSnapshotTableTopWidgets
                isLoadingData={isLoadingData}
                serpRecords={serpRecords}
                noSerpRecords={noSerpRecords}
            />
        </>
    );
};
